/**
 * Noor Integration Plugin
 * Integrates FastAPI authentication with Converse.js XMPP login
 */
import { converse } from '@converse/headless';
import { loginWithNoor, fetchUserProfile } from '../../shared/noor-auth.js';
import { getAuthData } from '../../shared/noor-db.js';
import NoorConfig from '../../shared/noor-config.js';

const { Strophe } = converse.env;

converse.plugins.add('converse-noor-integration', {
    dependencies: [],

    initialize() {
        const { _converse } = this;

        console.log('🔵 Noor Integration Plugin: initialize() called');

        // Profile refresh interval ID
        let profileRefreshInterval = null;

        /**
         * Start periodic profile refresh
         */
        const startProfileRefresh = async () => {
            if (!NoorConfig.profile.autoRefresh) {
                console.log('⏸️ Profile auto-refresh is disabled in config');
                return;
            }

            // Stop any existing interval
            if (profileRefreshInterval) {
                clearInterval(profileRefreshInterval);
            }

            console.log(`🔄 Starting profile auto-refresh every ${NoorConfig.profile.refreshInterval / 1000} seconds`);

            // Set up periodic refresh
            profileRefreshInterval = setInterval(async () => {
                try {
                    console.log('🔄 Auto-refreshing profile...');
                    const authData = await getAuthData();
                    if (authData && authData.username && authData.accessToken) {
                        await fetchUserProfile(authData.username, authData.accessToken);
                        console.log('✅ Profile auto-refresh successful');
                        
                        // Dispatch custom event to notify UI components
                        window.dispatchEvent(new CustomEvent('noor-profile-updated', {
                            detail: { timestamp: Date.now() }
                        }));
                    } else {
                        console.warn('⚠️ No auth data found, stopping auto-refresh');
                        clearInterval(profileRefreshInterval);
                        profileRefreshInterval = null;
                    }
                } catch (error) {
                    console.error('❌ Profile auto-refresh failed:', error);
                    if (!NoorConfig.profile.retryOnFailure) {
                        clearInterval(profileRefreshInterval);
                        profileRefreshInterval = null;
                    }
                }
            }, NoorConfig.profile.refreshInterval);
        };

        /**
         * Stop periodic profile refresh
         */
        const stopProfileRefresh = () => {
            if (profileRefreshInterval) {
                clearInterval(profileRefreshInterval);
                profileRefreshInterval = null;
                console.log('⏹️ Profile auto-refresh stopped');
            }
        };

        // Listen to the login hook
        // This hook is called in api.user.login() before attempting XMPP connection
        _converse.api.listen.on('login', async (_, payload) => {
            console.log('🟢 Noor Integration: login hook triggered', payload);
            
            // Extract credentials from payload and settings
            const jid = payload.jid;
            // Password might not be in payload, check settings too
            let password = payload.password || _converse.api.settings.get('password');
            
            if (!jid || !password) {
                console.log('Noor Integration: No credentials provided, skipping FastAPI auth');
                console.log('JID:', jid, 'Password:', password ? '<present>' : '<missing>');
                return payload; // Continue with normal flow
            }
            
            // Extract username from JID (remove domain if present)
            const username = jid.includes('@') ? jid.split('@')[0] : jid;
            
            // Attempt FastAPI authentication
            try {
                console.log('🔵 Noor Integration: Attempting FastAPI authentication for:', username);
                await loginWithNoor(username, password);
                console.log('✅ Noor Integration: FastAPI authentication successful');
                
                // Store credentials for auto-login if using session storage
                if (typeof window.storeCreds === 'function') {
                    window.storeCreds(jid, password);
                    console.log('💾 Credentials stored for auto-login');
                }

                // Start periodic profile refresh after successful login
                startProfileRefresh();
            } catch (noorAuthError) {
                console.error('❌ Noor Integration: FastAPI authentication failed:', noorAuthError);
                console.error('Error details:', noorAuthError.message, noorAuthError.stack);
                // Continue with XMPP login even if FastAPI fails (graceful degradation)
            }
            
            // Return payload without success flag to continue with normal XMPP login
            return payload;
        });

        // Hook into connection established
        _converse.api.listen.on('connected', () => {
            console.log('🟢 Noor Integration: XMPP connection established');
            // Start profile refresh if not already started
            if (!profileRefreshInterval) {
                startProfileRefresh();
            }
        });

        // Hook into disconnection
        _converse.api.listen.on('disconnected', () => {
            console.log('🔴 Noor Integration: XMPP disconnected');
            // Stop profile refresh on disconnect
            stopProfileRefresh();
        });

        console.log('✅ Noor Integration Plugin initialized');
    }
});
